// DevItemForm - §5 개발품 기본정보 등록 / 수정 폼.
//
// Dialog 안의 2열 폼이다(디자인 가이드 §7). §5 의 10개 항목이 한 화면에
// 들어가고, 라벨은 항상 입력 위 / 에러는 항상 입력 아래다.
//
// 등록 성공 직후 §16 자동일정을 실행한다 (REQ-ITEM-012). 체크박스는
// **기본 켜짐** 이다 - 담당자가 일정을 처음부터 손으로 쓰지 않게 하는 것이
// 목적이므로 opt-out 이어야 한다.
//
// 공정 Route 도 여기서 고른다. §7 이 "최초 개발품 등록 시 필요한 공정만
// 선택" 을 요구하고, Route 가 비면 상세화면의 `현재공정`(§18 #9) 과
// `생산계획 추가`(§18 #8) 가 둘 다 죽어서 §17 STEP 1->4 흐름이 끊긴다.
// 수정 모드에서는 Route 를 건드리지 않는다 - 순서 편집 진입점은 상세화면의
// [Route 편집] 하나여야 한다 (추적표 §22 중복 입력 금지).
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  Field,
  Select,
  TextInput,
  Textarea,
} from "@/components/ui";
import {
  type Customer,
  type DevItemDetail,
  type ProcessMaster,
  type Staff,
  autoScheduleDevItem,
  createDevItem,
  updateDevItem,
} from "@/lib/api";
import { useState } from "react";

interface FormState {
  customerId: string;
  projectName: string;
  partNo: string;
  partName: string;
  staffId: string;
  kickoffDate: string;
  sopDate: string;
  annualQty: string;
  note: string;
}

const blank: FormState = {
  customerId: "",
  projectName: "",
  partNo: "",
  partName: "",
  staffId: "",
  kickoffDate: "",
  sopDate: "",
  annualQty: "",
  note: "",
};

const fromDetail = (d: DevItemDetail): FormState => ({
  customerId: d.customerId,
  projectName: d.projectName,
  partNo: d.partNo,
  partName: d.partName,
  staffId: d.staffId,
  kickoffDate: d.kickoffDate,
  sopDate: d.sopDate,
  annualQty: d.annualQty ? String(d.annualQty) : "",
  note: d.note,
});

/**
 * 등록 폼의 Route 기본 선택. 활성 공정 전부를 켜 둔다 - 담당자는 빼는 쪽이
 * 더 빠르고(대부분의 품목이 대부분의 공정을 지난다), 무엇보다 아무것도
 * 선택하지 않은 채 등록되는 사고를 막는다.
 */
const defaultRoute = (processes: readonly ProcessMaster[]): string[] =>
  processes.filter((p) => p.active).map((p) => p.id);

/** 선택된 공정을 마스터 seq 순서로 정렬한다 (§7 원문 순서 보존). */
const orderedRoute = (
  processes: readonly ProcessMaster[],
  picked: readonly string[]
): string[] => processes.filter((p) => picked.includes(p.id)).map((p) => p.id);

/** 비활성 마스터는 새 선택지에서 빠지되 기존 참조는 유지된다 (§04 §7). */
const options = (rows: readonly { id: string; name: string; active: boolean }[], keep: string) =>
  rows.filter((r) => r.active || r.id === keep).map((r) => ({ value: r.id, label: r.name }));

export const DevItemForm = ({
  open,
  onClose,
  customers,
  staff,
  processes,
  edit,
  onSaved,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  customers: readonly Customer[];
  staff: readonly Staff[];
  /** 공정 마스터. 등록 시 Route 를 고르는 선택지다 (§7). */
  processes: readonly ProcessMaster[];
  /** 있으면 수정 모드 */
  edit?: DevItemDetail | null;
  onSaved: (item: DevItemDetail, message: string) => void;
  onError: (message: string) => void;
}) => {
  const [form, setForm] = useState<FormState>(() => (edit ? fromDetail(edit) : blank));
  const [routeIds, setRouteIds] = useState<readonly string[]>([]);
  const [autoSchedule, setAutoSchedule] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState("");

  // Dialog 를 다시 열 때 폼을 초기화한다. key 대신 seed 비교로 하는 이유:
  // 부모가 Dialog 를 언마운트하지 않고 open 만 토글하기 때문이다.
  const wanted = `${open}:${edit?.id ?? "new"}`;
  if (seed !== wanted) {
    setSeed(wanted);
    setForm(edit ? fromDetail(edit) : blank);
    setErrors({});
    setAutoSchedule(true);
    setRouteIds(defaultRoute(processes));
  }

  const set =
    <K extends keyof FormState>(k: K) =>
    (v: FormState[K]) =>
      setForm((f) => ({ ...f, [k]: v }));

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.customerId) e.customerId = "필수 항목을 입력해 주세요.";
    if (!form.staffId) e.staffId = "필수 항목을 입력해 주세요.";
    if (!form.partNo.trim()) e.partNo = "필수 항목을 입력해 주세요.";
    if (!form.partName.trim()) e.partName = "필수 항목을 입력해 주세요.";
    const qty = form.annualQty === "" ? 0 : Number.parseInt(form.annualQty, 10);
    if (Number.isNaN(qty) || qty < 0) e.annualQty = "0 이상의 수량을 입력해 주세요.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        customerId: form.customerId,
        projectName: form.projectName.trim(),
        partNo: form.partNo.trim(),
        partName: form.partName.trim(),
        staffId: form.staffId,
        kickoffDate: form.kickoffDate,
        sopDate: form.sopDate,
        annualQty: form.annualQty === "" ? 0 : Number.parseInt(form.annualQty, 10),
        note: form.note,
      };
      if (edit) {
        onSaved(await updateDevItem(edit.id, payload), "저장했어요.");
        onClose();
        return;
      }
      // 순서는 클릭 순서가 아니라 공정 마스터의 seq 순서다 (§7 원문 순서).
      let created = await createDevItem({
        ...payload,
        routeProcessIds: orderedRoute(processes, routeIds),
      });
      let message = "새 개발품을 등록했어요.";
      if (autoSchedule && form.sopDate) {
        const r = await autoScheduleDevItem(created.id, "fill_empty");
        created = r.item;
        message =
          r.adjustments.length > 0
            ? `등록이 끝났어요. 주요 일정을 자동으로 만들었어요. ${r.adjustments.length}건은 영업일로 조정했어요.`
            : "등록이 끝났어요. 공정 표준에 맞춰 주요 일정을 자동으로 만들었어요.";
      }
      onSaved(created, message);
      onClose();
    } catch (err) {
      onError(
        err instanceof Error ? err.message : "저장하지 못했어요. 잠시 후 다시 시도해 주세요."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={edit ? "개발품 수정" : "개발품 등록"}
      width={640}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button variant="primary" size="md" onClick={submit} loading={busy}>
            {edit ? "저장" : "등록하기"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-s4 sm:grid-cols-2">
        <Field label="고객사" required error={errors.customerId}>
          {(p) => (
            <Select
              {...p}
              value={form.customerId}
              onChange={set("customerId")}
              options={options(customers, form.customerId)}
              placeholder="선택"
            />
          )}
        </Field>
        <Field label="프로젝트">
          {(p) => (
            <TextInput
              {...p}
              value={form.projectName}
              onChange={set("projectName")}
              maxLength={120}
            />
          )}
        </Field>

        <Field label="품번" required error={errors.partNo}>
          {(p) => (
            <TextInput
              {...p}
              value={form.partNo}
              onChange={set("partNo")}
              placeholder="ABC-1234"
              maxLength={60}
            />
          )}
        </Field>
        <Field label="품명" required error={errors.partName}>
          {(p) => (
            <TextInput {...p} value={form.partName} onChange={set("partName")} maxLength={80} />
          )}
        </Field>

        <Field label="담당자" required error={errors.staffId}>
          {(p) => (
            <Select
              {...p}
              value={form.staffId}
              onChange={set("staffId")}
              options={options(staff, form.staffId)}
              placeholder="선택"
            />
          )}
        </Field>
        <Field label="개발착수일">
          {(p) => <DateField {...p} value={form.kickoffDate} onChange={set("kickoffDate")} />}
        </Field>

        <Field label="SOP" hint="양산 시작 예정일. 표준 일정 역산의 기준이 됩니다.">
          {(p) => <DateField {...p} value={form.sopDate} onChange={set("sopDate")} />}
        </Field>
        <Field label="연간수량" error={errors.annualQty}>
          {(p) => (
            <TextInput
              {...p}
              type="number"
              align="right"
              value={form.annualQty}
              onChange={set("annualQty")}
            />
          )}
        </Field>

        <Field label="비고" className="sm:col-span-2">
          {(p) => <Textarea {...p} value={form.note} onChange={set("note")} rows={2} />}
        </Field>

        {!edit && (
          <Field
            label="공정 Route"
            className="sm:col-span-2"
            hint="필요한 공정만 고르세요. 순서는 공정 마스터 순서를 따르고, 상세화면에서 조정할 수 있어요."
          >
            <div className="flex flex-wrap gap-x-s5 gap-y-s2">
              {processes
                .filter((pr) => pr.active)
                .map((pr) => (
                  <Checkbox
                    key={pr.id}
                    checked={routeIds.includes(pr.id)}
                    label={pr.name}
                    onChange={(on) =>
                      setRouteIds((prev) =>
                        on ? [...prev, pr.id] : prev.filter((x) => x !== pr.id)
                      )
                    }
                  />
                ))}
            </div>
          </Field>
        )}

        {!edit && (
          <div
            className="sm:col-span-2 pt-s3"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <Checkbox
              checked={autoSchedule && !!form.sopDate}
              disabled={!form.sopDate}
              onChange={setAutoSchedule}
              label="SOP 기준 표준 리드타임으로 기본일정 자동생성"
            />
            {!form.sopDate && (
              <p className="mt-s2 text-micro" style={{ color: "var(--text-tertiary)" }}>
                SOP 를 입력하면 표준 일정을 자동으로 만들 수 있어요.
              </p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
